// typescript
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import styled from '@mui/system/styled';
import { getIconComponentByName } from '@/utils/icons-map';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { FilterCheckboxData } from '@/types/common';

const StyledFormControlLabel = styled(FormControlLabel)({
  margin: '0 0 10px',
  width: '100%',
  'span': {
    color: '#606060',
    fontSize: '14px',
    letterSpacing: '0',
    lineHeight: 'normal',
  },
});

const StyledCheckbox = styled(Checkbox)({
  padding: '0',
  'svg': {
    width: '24px',
    height: '24px',
    marginRight: '8px',
  },
});

const StyledChildContainer = styled(Box)({
  paddingLeft: '28px',
  marginBottom: '6px',
  '> label span': {
    fontSize: '13px'
  }
})

const StyledExpandIcon = styled(IconButton)({
  padding: 0,
  minWidth: 'auto',
  marginLeft: 8,
  marginBottom: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '> svg': {
    width: 10,
    height: 10
  }
})

/**
 * Genera le checkbox per la facetta indicata da checkboxData.
 * Gestisce separatamente i casi di checkbox semplici da quelle annidate (famiglie e sottofamiglie).
 */
const mapFacetsToCheckbox = (
  facets: any,
  storeData: any,
  handleFilter: Function,
  checkboxData: FilterCheckboxData,
  index: number
) => {
  const baseQueryParams : {[key: string]: any} = {
    storeId: storeData.storeId,
    catalogId: storeData.catalogId,
    langId: storeData.langId,
    locale: storeData.locale,
    categoryId: storeData.categoryId,
    pageNumber: storeData.pageNumber,
    pageSize: storeData.pageSize
  }

  const childExists = Array.isArray(checkboxData.children) && checkboxData.children.length > 0;
  const isSelected = facets?.some((facet: any) => facet === checkboxData.value);
  const currentFilters = Array.isArray(facets) ? facets : [];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // caso checkbox semplice (non annidata)

    let queryParams: { [key: string]: any } = {
      storeId: storeData.storeId,
      catalogId: storeData.catalogId,
      langId: storeData.langId,
      locale: storeData.locale,
      categoryId: storeData.categoryId,
      pageNumber: storeData.pageNumber,
      pageSize: storeData.pageSize,
    };

    if (e.target.checked) {
      queryParams.filterValue = [...currentFilters, e.target.value];
    } else {
      queryParams.filterValue = currentFilters.filter((f: string) => f !== e.target.value);
    }

    handleFilter(queryParams);
  };

  // se non ci sono figli fallback semplice
  if (!childExists) {
    return (
      <StyledFormControlLabel
        key={`simple-${checkboxData.value ?? index}`}
        control={
          <StyledCheckbox
            checked={isSelected}
            icon={getIconComponentByName('checkbox')}
            checkedIcon={getIconComponentByName('checkboxChecked')}
            value={checkboxData.value}
            onChange={handleChange}
          />
        }
        label={checkboxData.label}
      />
    )
  }

  // componente per padre con figli: open dipende esclusivamente dal checked del padre o da figli già selezionati
  const CheckboxWithChildren: React.FC<{ item: any }> = ({ item }) => {
    const parentVal = item.value;
    const isParentSelected = facets?.some((facet: any) => facet === parentVal);
    const childSelected = item.children.some((c: any) => facets?.some((f: any) => f === c.value));
    // open iniziale: true se padre o un figlio è selezionato
    const [open, setOpen] = useState<boolean>(Boolean(isParentSelected || childSelected));

    // sincronizza open quando cambia selezione esterna
    useEffect(() => {
      const nowSelected = facets?.some((facet: any) => facet === parentVal) || item.children.some((c: any) => facets?.some((f: any) => f === c.value));
      setOpen(Boolean(nowSelected));
    }, [facets, parentVal]); // eslint-disable-line

    const queryParams : {[key: string]: any} = { ...baseQueryParams };
    const isIndeterminate = !isParentSelected && childSelected;

    const onParentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // se il padre viene selezionato, se ci sono dei figli già selezionati li rimuovo prima di aggiungere il padre
      if (e.target.checked) {
        const filtersWithoutChildren = currentFilters.filter((f: string) => !item.children.some((c: any) => c.value === f));
        const qp = { ...queryParams, filterValue: [...filtersWithoutChildren, e.target.value] };
        setOpen(true);
        handleFilter(qp);
        return;
      }

      // se il padre viene deselezionato, rimuovo solo il padre dall'elenco dei filtri
      const qp = { ...queryParams, filterValue: e.target.value };
      setOpen(Boolean(e.target.checked));
      handleFilter(qp);
    }

    return (
      <Box key={`parent-box-${parentVal}`}>
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: '6px' }}>
          <StyledFormControlLabel
            key={`parent-label-${parentVal}`}
            control={
              <StyledCheckbox
                checked={isParentSelected}
                indeterminate={isIndeterminate}
                indeterminateIcon={getIconComponentByName('checkboxIndeterminate')}
                icon={getIconComponentByName('checkbox')}
                checkedIcon={getIconComponentByName('checkboxChecked')}
                value={parentVal}
                onChange={onParentChange}
              />
            }
            label={item.label}
          />
          <StyledExpandIcon
            aria-label={open ? 'collapse children' : 'expand children'}
            onClick={(ev: React.MouseEvent) => {
              ev.stopPropagation();
              setOpen(prev => !prev);
            }}
            size="small"
          >
            {open ? getIconComponentByName('minus') : getIconComponentByName('plus')}
          </StyledExpandIcon>
        </Box>

        <Collapse in={open} timeout="auto" unmountOnExit key={`collapse-${parentVal}`}>
          <StyledChildContainer>
            {item.children.map((child: any) => {
              const childSelectedLocal = facets?.some((facet: any) => facet === child.value);
              return (
                <StyledFormControlLabel
                  key={`child-${parentVal}-${child.value}`}
                  control={
                    <StyledCheckbox
                      checked={childSelectedLocal || isParentSelected}
                      icon={getIconComponentByName('checkbox')}
                      checkedIcon={getIconComponentByName('checkboxChecked')}
                      value={child.value}
                      onChange={e => {
                        if (isParentSelected) {
                          const currentFilters = Array.isArray(facets) ? facets : [];
                          // Se il padre è selezionato, rimuovo il padre e aggiungo tutti i suoi figli tranne quello cliccato,
                          // aggiornando l'attuale elenco dei filtri e passandolo alla funzione di gestione del filtro come array
                          const filtersWithoutParent = currentFilters.filter((f: string) => f !== parentVal);
                          const allChildrenExceptClicked = item.children
                            .filter((c: any) => c.value !== child.value)
                            .map((c: any) => c.value);
                          const newFilters = [...filtersWithoutParent, ...allChildrenExceptClicked];
                          const qp = { ...queryParams, filterValue: newFilters };
                          handleFilter(qp);
                          return;
                        }

                        if (e.target.checked) {
                          // Altrimenti, quando il figlio viene selezionato, verifico se completa la selezione di tutti i figli
                          const currentFilters = Array.isArray(facets) ? facets : [];
                          const childrenValues = item.children.map((c: any) => c.value);
                          const potentialFilters = [...currentFilters, e.target.value];

                          const allChildrenSelected = childrenValues.every((childVal: string) =>
                            potentialFilters.includes(childVal)
                          );

                          if (allChildrenSelected) {
                            // Se tutti i figli verrebbero selezionati, seleziono il padre e deseleziono tutti i figli
                            const filtersWithoutChildren = currentFilters.filter((f: string) => !item.children.some((c: any) => c.value === f));
                            const qp = { ...queryParams, filterValue: [...filtersWithoutChildren, parentVal] };
                            handleFilter(qp);
                            return;
                          }
                        }

                        // Altrimenti gestisco normalmente, aggiungendo o togliendo il figlio a seconda dello stato della checkbox,
                        // passando alla funzione di gestione del filtro il valore singolo
                        const qp = { ...queryParams, filterValue: e.target.value };
                        handleFilter(qp);
                      }}
                    />
                  }
                  label={child.label}
                />
              )
            })}
          </StyledChildContainer>
        </Collapse>
      </Box>
    )
  }

  return <CheckboxWithChildren key={`parent-${checkboxData.value ?? index}`} item={checkboxData} />;
}

export default mapFacetsToCheckbox;
